import React, { useMemo, useRef, useEffect, useCallback } from "react";
import SVGraphics from "../../../assets/SVGraphics";
import { useViewer } from "../DicomViewerNew/context/ViewerContext";

const AnnotationMarkers = ({
  annotationData,
  currentImageIndex,
  viewport,
  isReady,
}) => {
  const { selectedAnnotationId, setSelectedAnnotationId, zoomToAnnotation } =
    useViewer();
  const containerRef = useRef(null);

  // Only annotations for the current slice
  const annsOnSlice = annotationData[currentImageIndex] || [];

  // Build markers purely from annotations (no nodulesData)
  const markers = useMemo(() => {
    return annsOnSlice
      .filter((a) => a.type !== "personal") // Ignore personal notes
      .map((a) => {
        let start = a.start,
          end = a.end;
        try {
          if (typeof start === "string") start = JSON.parse(start);
          if (typeof end === "string") end = JSON.parse(end);
        } catch (_) {}

        const sx = Array.isArray(start) ? start[0] : start?.x;
        const sy = Array.isArray(start) ? start[1] : start?.y;
        const ex = Array.isArray(end) ? end[0] : end?.x;
        const ey = Array.isArray(end) ? end[1] : end?.y;

        const mx = sx && ex ? (sx + ex) / 2 : a.x;
        const my = sy && ey ? (sy + ey) / 2 : a.y;

        return {
          id: a.id,
          type: "annotation",
          length: a.length,
          midpoint: { x: mx, y: my },
        };
      });
  }, [annsOnSlice]);

  // Update marker positions on the canvas
  const updatePositions = useCallback(() => {
    if (!viewport || !containerRef.current) return;

    markers.forEach((marker) => {
      const el = containerRef.current.querySelector(
        `[data-marker-id="${marker.id}"]`
      );
      if (!el) return;

      const { x, y } = marker.midpoint;
      try {
        const [canvasX, canvasY] = viewport.worldToCanvas([x, y, 0]);
        el.style.left = `${canvasX}px`;
        el.style.top = `${canvasY}px`;
        el.style.opacity = 1;
      } catch {
        el.style.opacity = 0;
      }
    });
  }, [viewport, markers]);

  useEffect(() => {
    if (!viewport || !containerRef.current) return;
    const elements = containerRef.current.querySelectorAll("[data-marker-id]");
    elements.forEach((el) => (el.style.opacity = 0));
    const id = requestAnimationFrame(() => updatePositions());
    return () => cancelAnimationFrame(id);
  }, [viewport, markers, updatePositions]);

  useEffect(() => {
    if (!viewport?.element) return;
    viewport.element.addEventListener(
      "CORNERSTONE_CAMERA_MODIFIED",
      updatePositions
    );
    return () => {
      viewport.element.removeEventListener(
        "CORNERSTONE_CAMERA_MODIFIED",
        updatePositions
      );
    };
  }, [viewport, updatePositions]);

  if (!isReady || !viewport) return null;

  return (
    <div ref={containerRef} data-annotation-container>
      {markers.map((m) => {
        const isSelected = m.id === selectedAnnotationId;

        return (
          <div
            key={m.id}
            data-marker-id={m.id}
            onClick={(e) => {
              e.stopPropagation();
              const isNowSelected = m.id !== selectedAnnotationId;
              setSelectedAnnotationId(isNowSelected ? m.id : null);
              zoomToAnnotation(isNowSelected ? m : null);
            }}
            style={{
              position: "absolute",
              opacity: 0,
              transition: "opacity 0.15s ease-out",
              width: 36,
              height: 36,
              background: isSelected ? "#5A3EFF" : "white",
              borderRadius: "50%",
              transform: "translate(-50%, -50%)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(0,0,0,.45)",
              zIndex: 49,
            }}
            title={
              typeof m.length === "number"
                ? `Length: ${m.length.toFixed(1)} mm`
                : "Annotation Marker"
            }>
            <SVGraphics
              svgname={
                isSelected
                  ? "MeasureAnnotationIconActive"
                  : "MeasureAnnotationIcon"
              }
              width="17px"
              height="17px"
            />
          </div>
        );
      })}
    </div>
  );
};

export default AnnotationMarkers;
